/**
 * RacksFrontView — vista «Frontal» del listado de Racks.
 *
 * Es la vista adicional del listado: los armarios filtrados se pintan lado a
 * lado, tal cual se ven de frente, con cada equipo en su U real y los cables
 * que salen de sus puertos (latiguillos, fibra entre armarios y cable de pared).
 *
 * Los datos son los MISMOS del diseñador de cableado (`/api/cabling/graph`),
 * así que no hay una segunda verdad: si un latiguillo cambia, aquí se ve.
 *
 * Geometría: `wiring/rackGeometry.js` (compartida con el diseñador).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/api';
import {
  RACK_HEADER,
  U_H,
  buildRackLayout,
  rackCablePoints,
  rackPortOffset,
} from './wiring/rackGeometry';
import './RacksFrontView.css';

const KIND_META = {
  switch: { icon: '🔀', color: '#2563eb', label: 'Switch' },
  panel: { icon: '🔌', color: '#7c3aed', label: 'Patch panel' },
  router: { icon: '🌐', color: '#0891b2', label: 'Router' },
  firewall: { icon: '🛡️', color: '#dc2626', label: 'Firewall' },
  server: { icon: '🖧', color: '#0f766e', label: 'Servidor' },
  storage: { icon: '💾', color: '#b45309', label: 'Cabina' },
  outlet: { icon: '🏢', color: '#16a34a', label: 'Toma' },
  workstation: { icon: '🖥️', color: '#475569', label: 'Equipo' },
  printer: { icon: '🖨️', color: '#64748b', label: 'Impresora' },
  ip_phone: { icon: '☎️', color: '#0ea5e9', label: 'Teléfono' },
  wifi_ap: { icon: '📶', color: '#8b5cf6', label: 'AP WiFi' },
};

const EDGE_LABEL = {
  port_connection: 'Latiguillo',
  backbone: 'Fibra entre armarios',
  wall_run: 'Cable de pared',
};

function truncate(text, max) {
  const value = String(text || '');
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

function colorHex(color) {
  if (!color) return '#64748b';
  if (color.startsWith('#')) return color;
  const map = {
    azul: '#2563eb',
    rojo: '#dc2626',
    verde: '#16a34a',
    amarillo: '#eab308',
    naranja: '#ea580c',
    gris: '#64748b',
    negro: '#111827',
    blanco: '#e5e7eb',
    morado: '#7c3aed',
    violeta: '#8b5cf6',
    rosa: '#ec4899',
    marron: '#92400e',
  };
  return map[String(color).toLowerCase()] || '#64748b';
}

/** Ruta del cable en la vista de armarios (canal lateral + pasillo). */
function cablePath(points) {
  if (!points || !points.length) return '';
  return points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
}

function RacksFrontView({ companyId, racks = [], onOpenRack }) {
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const wrapRef = useRef(null);

  const rackIds = useMemo(
    () => (racks || []).map((rack) => rack.id).filter(Boolean).sort((a, b) => a - b),
    [racks],
  );
  const scopeKey = rackIds.length ? `racks:${rackIds.join(',')}` : 'company';
  // La empresa se toma de los propios racks: al cambiar de empresa en la barra
  // superior, la lista se recarga antes que la prop y se pedía el grafo de la
  // empresa anterior con los racks nuevos (salía vacío).
  const rackCompanyId = racks?.[0]?.company_id || companyId;

  useEffect(() => {
    if (!rackCompanyId) { setGraph(null); return undefined; }
    let cancelled = false;
    setLoading(true);
    setError('');
    const params = { scope_key: scopeKey, include_unconnected: true };
    if (rackIds.length) params.rack_ids = rackIds.join(',');
    api.getCablingGraph(params, rackCompanyId)
      .then((res) => { if (!cancelled) setGraph(res?.data || { nodes: [], edges: [] }); })
      .catch((e) => { if (!cancelled) { setError(e.message); setGraph(null); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [rackCompanyId, scopeKey, rackIds]);

  const nodes = useMemo(() => graph?.nodes || [], [graph]);
  const edges = useMemo(() => graph?.edges || [], [graph]);

  const rackMetas = useMemo(() => {
    const fromGraph = graph?.scope?.racks || [];
    if (fromGraph.length) return fromGraph;
    return racks.map((rack) => ({
      id: rack.id,
      name: rack.name,
      code: rack.code,
      height_u: rack.height_u || 42,
    }));
  }, [graph, racks]);

  // Solo los equipos que van montados con una U asignada.
  const rackNodes = useMemo(
    () => nodes.filter((node) => node.rack_id && node.position_u),
    [nodes],
  );
  const outside = nodes.length - rackNodes.length;

  const layout = useMemo(
    () => buildRackLayout(rackNodes, rackMetas),
    [rackNodes, rackMetas],
  );

  const portIndex = useMemo(() => {
    const map = new Map();
    nodes.forEach((node) => {
      (node.ports || []).forEach((port, index) => {
        map.set(port.key, { node, port, index });
      });
    });
    return map;
  }, [nodes]);

  const totalW = layout.boxes.length
    ? layout.boxes[layout.boxes.length - 1].x + layout.boxes[layout.boxes.length - 1].width + 40
    : 0;
  const totalH = layout.boxes.length
    ? Math.max(...layout.boxes.map((box) => box.y + box.height)) + 40
    : 0;

  const edgeGeoms = useMemo(() => edges.map((edge) => {
    const a = portIndex.get(edge.a);
    const b = portIndex.get(edge.b);
    if (!a || !b) return null;
    const pa = layout.positions[a.node.key];
    const pb = layout.positions[b.node.key];
    if (!pa || !pb) return null;
    const ga = layout.geos.get(a.node.key);
    const gb = layout.geos.get(b.node.key);
    if (!ga || !gb) return null;
    const ua = rackPortOffset(ga, a.index);
    const ub = rackPortOffset(gb, b.index);
    const points = rackCablePoints(
      pa.x + ua.x, pa.y + ua.y,
      pb.x + ub.x, pb.y + ub.y,
      layout.boxByNode.get(a.node.key),
      layout.boxByNode.get(b.node.key),
    );
    return { edge, d: cablePath(points) };
  }).filter(Boolean), [edges, portIndex, layout]);

  // El contenedor raíz SIEMPRE se pinta: si desaparece durante la carga, el
  // observador de tamaño se queda sin elemento y la vista no se ajusta nunca.
  const state = loading
    ? { text: 'Cargando el frontal de los armarios…' }
    : error
      ? { text: error, error: true }
      : !layout.boxes.length
        ? {
          text: 'Ningún equipo de estos armarios tiene posición U.',
          icon: '🗄',
          sub: 'Asigna la U en la ficha de cada elemento (campo «Posición U») y aquí aparecerá colocado.',
        }
        : null;

  return (
    <div className="rfv" ref={wrapRef}>
      {state ? (
        <div className={`rfv-state${state.error ? ' is-error' : ''}`}>
          {state.icon && <div className="rfv-state-ic">{state.icon}</div>}
          {state.text}
          {state.sub && <div className="rfv-state-sub">{state.sub}</div>}
        </div>
      ) : (
        <>
      <div className="rfv-head">
        <span className="rfv-legend"><i className="rfv-line is-patch" /> Latiguillo</span>
        <span className="rfv-legend"><i className="rfv-line is-fiber" /> Fibra entre armarios</span>
        <span className="rfv-legend"><i className="rfv-line is-wall" /> Cable de pared</span>
        <span className="rfv-count">
          {layout.boxes.length} armarios · {rackNodes.length} equipos · {edgeGeoms.length} cables
          {outside > 0 ? ` · ${outside} sin U` : ''}
        </span>
      </div>

      <div className="rfv-scroll">
        <svg
          className="rfv-svg"
          width={Math.round(totalW)}
          height={Math.round(totalH)}
          viewBox={`0 0 ${totalW} ${totalH}`}
          role="img"
          aria-label="Vista frontal de los armarios"
        >
          {layout.boxes.map((box) => {
            const rack = racks.find((item) => item.id === box.rackId);
            return (
              <g key={box.key} className="rfv-rack">
                <rect
                  className="rfv-frame"
                  x={box.x}
                  y={box.y}
                  width={box.width}
                  height={box.height}
                  rx={12}
                  onClick={() => rack && onOpenRack?.(rack)}
                >
                  <title>{`${box.title} · ${box.heightU}U`}</title>
                </rect>
                <text className="rfv-title" x={box.x + 12} y={box.y + 20}>
                  {truncate(box.code ? `${box.code} · ${box.name}` : box.name, 52)}
                </text>
                {Array.from({ length: box.heightU }, (_, i) => {
                  const u = box.heightU - i;
                  const y = box.y + RACK_HEADER + i * U_H;
                  return (
                    <g key={`slot-${box.key}-${u}`}>
                      <line className="rfv-slot" x1={box.x + 6} x2={box.x + box.width - 6} y1={y} y2={y} />
                      {u % 5 === 0 && (
                        <text className="rfv-u" x={box.x + box.width - 6} y={y + 10} textAnchor="end">
                          {u}U
                        </text>
                      )}
                    </g>
                  );
                })}
              </g>
            );
          })}

          {edgeGeoms.map(({ edge, d }) => (
            <path
              key={`${edge.kind}-${edge.linked_id}-${edge.a}-${edge.b}`}
              className={`rfv-cable is-${edge.kind}${edge.kind !== 'wall_run' && String(edge.cable_type || '').toLowerCase().includes('fibra') ? ' is-fiber' : ''}`}
              d={d}
              stroke={edge.kind === 'backbone' ? undefined : colorHex(edge.cable_color)}
            >
              <title>{`${EDGE_LABEL[edge.kind] || edge.kind}${edge.cable_type ? ` · ${edge.cable_type}` : ''}`}</title>
            </path>
          ))}

          {rackNodes.map((node) => {
            const pos = layout.positions[node.key];
            const geo = layout.geos.get(node.key);
            if (!pos || !geo) return null;
            const meta = KIND_META[node.kind] || { icon: '❓', color: '#64748b', label: node.kind };
            const ports = node.ports || [];
            const used = ports.filter((port) => (port.edges || []).length || port.external).length;
            return (
              <g
                key={node.key}
                className="rfv-unit"
                transform={`translate(${pos.x},${pos.y})`}
                style={{ '--kind': meta.color }}
              >
                <rect className="rfv-unit-rect" width={geo.width} height={geo.height} rx={4} />
                <rect className="rfv-unit-bar" width={4} height={geo.height} rx={2} />
                <text className="rfv-unit-name" x={10} y={geo.height / 2 + 3}>
                  {truncate(node.title, 18)}
                </text>
                <text className="rfv-unit-u" x={geo.width - 8} y={geo.height / 2 + 3} textAnchor="end">
                  {node.size_u > 1 ? `${node.position_u}-${node.position_u + node.size_u - 1}U` : `${node.position_u}U`}
                </text>
                <title>{`${node.title}\n${meta.label} · U${node.position_u}${node.size_u > 1 ? ` (${node.size_u}U)` : ''}\n${used}/${ports.length} puertos cableados`}</title>
                {ports.map((port, index) => {
                  const col = index % geo.cols;
                  const row = Math.floor(index / geo.cols);
                  const x = geo.padX + col * (geo.portW + geo.portGap);
                  const y = row * geo.rowH + geo.rowH / 2 - geo.portH / 2;
                  const busy = (port.edges || []).length > 0 || port.external;
                  return (
                    <g key={port.key} className={`rfv-port${busy ? ' is-busy' : ''}`}>
                      <rect x={x} y={y} width={geo.portW} height={geo.portH} rx={1.5} />
                      <title>{`${node.title} · ${port.label}${busy ? ' · con cable' : ' · libre'}`}</title>
                    </g>
                  );
                })}
              </g>
            );
          })}
        </svg>
      </div>
        </>
      )}
    </div>
  );
}

export default RacksFrontView;
